export const YOY_PERIODS_MONTHLY = 12;
export const YOY_PERIODS_WEEKLY = 52;

export type DateGranularity = 'week' | 'month';

export function getYoYPeriodOffset(granularity: DateGranularity): number {
  return granularity === 'week' ? YOY_PERIODS_WEEKLY : YOY_PERIODS_MONTHLY;
}

/**
 * Infers YoY period offset from the API date refs. Monthly series use 12-period
 * lookback; weekly series use 52-period. Date format: YYYY-MM => monthly,
 * YYYY-MM-DD => weekly.
 */
export function inferYoYPeriodOffsetFromDateRefs(
  dateRefs: string[] | undefined
): number {
  if (!dateRefs || dateRefs.length === 0) {
    return YOY_PERIODS_MONTHLY;
  }
  const first = dateRefs[0];
  return typeof first === 'string' && first.length === 10
    ? YOY_PERIODS_WEEKLY
    : YOY_PERIODS_MONTHLY;
}

/**
 * Returns YoY growth at the latest point: (current - yearAgo) / yearAgo.
 * Uses periodOffset to determine lookback (12 for monthly, 52 for weekly).
 */
export function getLatestYoYGrowth(
  values: number[],
  periodOffset: number
): number | null {
  const minLen = periodOffset + 1;
  if (values.length < minLen) return null;
  const current = values[values.length - 1];
  const yearAgo = values[values.length - 1 - periodOffset];
  if (
    current !== null &&
    current !== undefined &&
    current !== 0 &&
    yearAgo !== null &&
    yearAgo !== undefined &&
    yearAgo !== 0
  ) {
    return (current - yearAgo) / yearAgo;
  }
  return null;
}

/**
 * Returns YoY growth at a given index: (value[idx] - value[idx - periodOffset]) / value[idx - periodOffset].
 */
export function getYoYGrowthAt(
  values: number[],
  index: number,
  periodOffset: number
): number | null {
  if (index < periodOffset) return null;
  const current = values[index];
  const yearAgo = values[index - periodOffset];
  if (
    current !== null &&
    current !== undefined &&
    current !== 0 &&
    yearAgo !== null &&
    yearAgo !== undefined &&
    yearAgo !== 0
  ) {
    return (current - yearAgo) / yearAgo;
  }
  return null;
}
